/**
 * Shaping for the Tracking page's Pricing accuracy tab (get_pricing_accuracy
 * RPC in supabase/migrations/20261027140000_dashboard_reports.sql). Its
 * population and tolerance are get_owner_pricing's, so these figures agree
 * with Analytics > Pricing quality for the same dates. Every number names the
 * pricing_lines records behind it.
 */

import { share, SOURCE_LABELS, type DrillSpec, type PriceSource } from '../analytics/owner/ownerData';
import { dayLabel, periodText } from './opsHistory';

type Confidence = 'high' | 'medium' | 'low' | 'very_low' | 'unknown';

interface PricingCounts {
  lines: number;
  within: number;
  median_gap: number | null;
}

export interface PricingAccuracy {
  generated_at: string;
  range: { from: string; to: string };
  grain: 'day' | 'week';
  totals: { quoted_lines: number; priced: number; within: number; median_gap: number | null };
  series: Array<PricingCounts & { period: string; from: string; to: string; quoted_lines: number }>;
  by_source: Array<PricingCounts & { source: PriceSource | null }>;
  by_confidence: Array<PricingCounts & { confidence: Confidence; quoted_lines: number }>;
}

/**
 * The tolerance, worded as on Analytics > Pricing quality so the two pages
 * read alike (owner_within_tolerance = the pricingThresholds.js rule).
 */
export const TOLERANCE_TEXT =
  'A line counts as sent at the system’s price when the price sent is within 2% of the suggestion, or within a minimum by price band (S$0.50 up to S$5, S$1 up to S$50, S$5 up to S$200, S$10 up to S$500, S$15 above).';

/** Below this many priced lines a period's percentage is noise; the chart shows it faintly. */
export const MIN_LINES = 10;

/** The share the owner expects to be sent at the system's price. */
export const TARGET_PCT = 80;

export const PRICING_RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
] as const;

export type PricingDays = (typeof PRICING_RANGES)[number]['days'];

export const CONFIDENCE_LABELS: Record<Confidence, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  very_low: 'Very low',
  unknown: 'Not recorded',
};

export const sourceLabel = (source: PriceSource | null): string => (source ? SOURCE_LABELS[source] ?? source : 'No source recorded');

/** The median gap is a fraction of the price sent: 0.083 -> '8.3%'. */
export const formatGap = (gap: number | null | undefined): string => (gap == null ? '-' : `${(gap * 100).toFixed(1)}%`);

export interface SeriesRow {
  period: string;
  from: string;
  to: string;
  label: string;
  quoted: number;
  lines: number;
  within: number;
  unpriced: number;
  gap: number | null;
  /** Accuracy where the period has at least MIN_LINES priced lines, else null. */
  pct: number | null;
  /** Accuracy of a period with 1..MIN_LINES-1 priced lines, drawn faintly; else null. */
  pctSmall: number | null;
}

/** Chart rows: a percentage only where it rests on enough lines. */
export function seriesRows(p: Pick<PricingAccuracy, 'series' | 'grain'>): SeriesRow[] {
  return p.series.map((s) => {
    const pct = share(s.within, s.lines);
    return {
      period: s.period,
      from: s.from,
      to: s.to,
      label: p.grain === 'week' ? `w/c ${dayLabel(s.period)}` : dayLabel(s.period),
      quoted: s.quoted_lines,
      lines: s.lines,
      within: s.within,
      unpriced: Math.max(0, s.quoted_lines - s.lines),
      gap: s.median_gap,
      pct: s.lines >= MIN_LINES ? pct : null,
      pctSmall: s.lines > 0 && s.lines < MIN_LINES ? pct : null,
    };
  });
}

type LineFilter = { source?: PriceSource; confidence?: Confidence; within?: boolean; priced?: boolean };

/** The pricing_lines records behind a number. */
export function pricingDrill(title: string, from: string, to: string, filter: LineFilter = {}, note?: string): DrillSpec {
  const f: Record<string, string | boolean> = {};
  for (const [k, v] of Object.entries(filter)) if (v != null) f[k] = v;
  return { title: `${title}, ${periodText(from, to)}`, kind: 'pricing_lines', from, to, ...(Object.keys(f).length ? { filter: f } : {}), ...(note ? { note } : {}) };
}

export interface ConfidenceRow {
  confidence: Confidence;
  label: string;
  quoted: number;
  lines: number;
  within: number;
  gap: number | null;
  pct: number | null;
  /** Fewer than MIN_LINES priced lines: shown, not judged. */
  small: boolean;
}

export function confidenceRows(p: Pick<PricingAccuracy, 'by_confidence'>): ConfidenceRow[] {
  return p.by_confidence.map((c) => ({
    confidence: c.confidence,
    label: CONFIDENCE_LABELS[c.confidence] ?? c.confidence,
    quoted: c.quoted_lines,
    lines: c.lines,
    within: c.within,
    gap: c.median_gap,
    pct: share(c.within, c.lines),
    small: c.lines < MIN_LINES,
  }));
}

/**
 * Why the "Very low" row looks broken (hundreds of lines, a handful priced, a
 * huge gap): very_low is the label the pricing ladder writes when it found no
 * price in DBR's records (unpricedPriceSources.buildNoPriceDetails), so those
 * lines normally go out with no system price at all. Null when there are none.
 */
export function veryLowNote(rows: ConfidenceRow[]): string | null {
  const r = rows.find((c) => c.confidence === 'very_low');
  if (!r || r.quoted === 0) return null;
  const n = (x: number) => x.toLocaleString('en-SG');
  const unpriced = r.quoted - r.lines;
  const priced =
    r.lines === 0 ? '' : r.small ? `; the ${n(r.lines)} it did price ${r.lines === 1 ? 'is' : 'are'} too few to judge, so ignore the gap` : `; ${n(r.lines)} were priced anyway`;
  return `Very low is the label the system gives a line it found no price for in DBR’s records: ${n(unpriced)} of these ${n(r.quoted)} lines went out with no system price${priced}.`;
}

/**
 * One plain sentence on whether the confidence label predicts accuracy:
 * compares the graded buckets (high > medium > low > very low) that have
 * enough lines. Null when fewer than two buckets can be compared.
 */
export function confidenceVerdict(rows: ConfidenceRow[]): string | null {
  const graded = rows.filter((r) => r.confidence !== 'unknown' && !r.small && r.pct != null);
  if (graded.length < 2) return null;
  const top = graded[0];
  const bottom = graded[graded.length - 1];
  const ordered = graded.every((r, i) => i === 0 || (r.pct as number) <= (graded[i - 1].pct as number));
  const spread = (top.pct as number) - (bottom.pct as number);
  const pair = `${top.label} confidence: ${Math.round(top.pct as number)}% sent at the system’s price; ${bottom.label.toLowerCase()}: ${Math.round(bottom.pct as number)}%.`;
  if (ordered && spread >= 10) return `Yes - the label tracks accuracy. ${pair}`;
  if (spread <= 5 && spread >= -5) return `Not much - every level is sent at the system’s price about as often. ${pair}`;
  return `Only partly - higher confidence is not reliably more accurate. ${pair}`;
}
